import { 
  doc, 
  getDoc,
  Timestamp, 
  GeoPoint,
} from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { db } from '../../lib/firebase';
import { DefaultValues } from './types';

// Check if we're in development mode
const isDevelopment = process.env.NODE_ENV === 'development' || 
                     window.location.hostname === 'localhost' || 
                     window.location.hostname === '127.0.0.1';

/**
 * Check if current user is an admin
 */
export const isUserAdmin = async (): Promise<boolean> => {
  try {
    const auth = getAuth();
    const user = auth.currentUser;
    
    if (!user) return false;
    
    // In development, allow all operations
    if (isDevelopment) {
      console.log("Development environment detected - bypassing admin check");
      return true;
    }
    
    // Check the user's admin status directly from Firestore
    const userDoc = await getDoc(doc(db, 'users', user.uid));
    if (userDoc.exists()) {
      return userDoc.data().isAdmin === true;
    }
    
    return false;
  } catch (error) {
    console.error("Error checking admin status:", error);
    return false;
  }
};

/**
 * Converts Firestore types to serializable values
 */
export const convertToSerializable = (data: unknown): unknown => {
  if (!data) return data;
  
  if (data instanceof GeoPoint) {
    return {
      _type: 'geopoint',
      latitude: data.latitude,
      longitude: data.longitude
    };
  }
  
  if (data instanceof Timestamp) {
    return {
      _type: 'timestamp',
      seconds: data.seconds,
      nanoseconds: data.nanoseconds
    };
  }
  
  if (data instanceof Date) {
    return {
      _type: 'date',
      value: data.toISOString()
    };
  }
  
  if (Array.isArray(data)) {
    return data.map(item => convertToSerializable(item));
  }
  
  if (typeof data === 'object' && data !== null) {
    const result: Record<string, unknown> = {};
    for (const key in data) {
      if (Object.prototype.hasOwnProperty.call(data, key)) {
        result[key] = convertToSerializable((data as Record<string, unknown>)[key]);
      }
    }
    return result;
  }
  
  return data;
};

/**
 * Converts serialized values back to Firestore types
 */
export const convertFromSerializable = (data: unknown): unknown => {
  if (!data) return data;
  
  const objData = data as Record<string, any>;

  if (objData && typeof objData === 'object' && objData._type === 'geopoint') {
    return new GeoPoint(objData.latitude as number, objData.longitude as number);
  }
  
  if (objData && typeof objData === 'object' && objData._type === 'timestamp') {
    return new Timestamp(objData.seconds as number, objData.nanoseconds as number);
  }
  
  if (objData && typeof objData === 'object' && objData._type === 'date') {
    return new Date(objData.value as string);
  }
  
  if (Array.isArray(data)) {
    return data.map(item => convertFromSerializable(item));
  }
  
  if (typeof data === 'object' && data !== null) {
    const result: Record<string, unknown> = {};
    for (const key in data) {
      if (Object.prototype.hasOwnProperty.call(data, key)) {
        result[key] = convertFromSerializable((data as Record<string, unknown>)[key]);
      }
    }
    return result;
  }
  
  return data;
};

/**
 * Applies default values for missing fields based on collection type
 * @param data - The document data to check
 * @param collectionType - The collection type (e.g., 'drinks', 'locations')
 * @param logFn - Optional logging function
 * @returns Object with default values applied for missing fields
 */
export const applyDefaultValues = (
  data: Record<string, any>,
  collectionType: keyof DefaultValues,
  logFn?: (message: string) => void
): Record<string, any> => {
  // Define default values for different collection types
  const defaults: DefaultValues = {
    // Core collections
    flavors: {
      name: 'Unnamed Flavor',
      groupId: 999, // High number to put at end
      orderInGroup: 999
    },
    attributes: {
      name: 'Unnamed Attribute',
      groupId: 999,
      orderInGroup: 999
    },
    brands: {
      name: 'Unnamed Brand',
      brandSize: 'unknown'
    },
    drinks: {
      name: 'Unnamed Drink',
      brandId: '',
      brandName: '',
      flavors: [],
      attributes: []
    },
    locations: {
      name: 'Unnamed Location',
      address: '',
      url: null,
      locationType: "other",
      coordinates: new GeoPoint(0, 0),
      googlePlaceId: null,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    },
    sightings: {
      locationId: '',
      locationName: '',
      drinkId: '',
      drinkName: '',
      brandId: '',
      firstSighted: Timestamp.now()
    },
    users: {
      username: 'user',
      permissions: ['user'],
      profilePictureUrl: null,
      uiTheme: null,
      createdAt: Timestamp.now(),
      updatedAt: null,
      isAdmin: false,
      email: null
    },
    permissions: {
      identifier: 'unnamed_permission',
      description: 'Unnamed permission'
    },
    reviews: {
      userId: '',
      username: '',
      drinkId: '',
      drinkName: '',
      rating: 0,
      reviewText: null,
      isPublic: true,
      locationId: null,
      locationName: null,
      date: Timestamp.now()
    },
    systemSettings: {
      algolia: {
        syncMode: 'normal',
        incrementalSyncEnabled: true,
        lastFullSyncTime: null,
        lastUpdated: null
      }
    },
    
    // Subcollections
    visitedLocations: {
      locationId: '',
      locationName: '',
      rating: 0,
      publicNote: null,
      privateNote: null,
      visitDate: Timestamp.now()
    },
    confirmations: {
      timestamp: Timestamp.now(),
      userId: '',
      username: '',
      createdAt: Timestamp.now()
    }
  };
  
  if (!defaults[collectionType]) {
    logFn?.(`Warning: No default values defined for collection type: ${collectionType}`);
    return data;
  }
  
  // Apply defaults where fields are missing
  const result = { ...data };
  const defaultFields = defaults[collectionType];
  
  Object.entries(defaultFields).forEach(([field, defaultValue]) => {
    if (result[field] === undefined) {
      result[field] = defaultValue;
      logFn?.(`Added default value for missing field '${field}' in ${collectionType}`);
    }
  });
  
  return result;
}; 